import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../../components/ui/Button';
import { EmailFormProps, UserData } from '../../types';
import { loadUserData } from '../../utils/localStorage';
import { getUTMParameters } from '../../utils/utmUtils';
import { submitQuizResults } from '../../utils/submitQuizResults';

const FormContainer = styled.div`
  width: 100%;
  max-width: 60rem;
  margin: 3.2rem auto 3.2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  font-size: 1.6rem;
  font-weight: 500;
  margin-bottom: 0.8rem;
  color: var(--color-darkest);
  font-family: var(--font-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 1.2rem;
  border: 1px solid var(--color-medium);
  border-radius: 0.5rem;
  font-size: 16px;
  font-family: var(--font-secondary);
  color: var(--color-darkest);

  &:focus {
    outline: none;
    border-color: var(--color-theme);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
    font-size: 16px;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const CheckboxInput = styled.input`
  margin-top: 0.4rem;
  margin-right: 1rem;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 1.4rem;
  line-height: 1.6;
  color: var(--color-darkest);
  font-family: var(--font-secondary);

  a {
    color: var(--color-theme);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 1.4rem;
  margin-top: 0.5rem;
  font-family: var(--font-secondary);
`;

const IntroText = styled.div`
  margin-bottom: 3rem;
  padding: 2rem;
  background-color: rgba(212, 175, 55, 0.05);
  border-radius: 0.8rem;
  border-left: 4px solid var(--color-theme);

  p {
    font-size: 1.6rem;
    line-height: 1.6;
    color: var(--color-darkest);
    font-family: var(--font-secondary);
    margin-bottom: 1.5rem;
    text-align: justify;

    @media (max-width: 800px) {
      font-size: 1.9rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SubmissionError = styled.div`
  color: #e74c3c;
  font-size: 1.6rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.2rem;
  background-color: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 0.5rem;
  font-family: var(--font-secondary);
  text-align: center;
`;

const EmailForm: React.FC<EmailFormProps> = ({ onSubmit, onRestart, categoryCounts, answers, questions }) => {
  const [email, setEmail] = useState('');
  const [forename, setForename] = useState('');
  const [surname, setSurname] = useState('');
  const [telephone, setTelephone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    forename: '',
    surname: '',
    telephone: '',
  });

  // Load saved user data on component mount
  useEffect(() => {
    const userData = loadUserData();
    if (userData) {
      if (userData.email) setEmail(userData.email);
      if (userData.forename) setForename(userData.forename);
      if (userData.surname) setSurname(userData.surname);
      if (userData.telephone) setTelephone(userData.telephone);
    }
  }, []);

  useEffect(() => {
    if (email.toLowerCase() === '0000000000') {
      onRestart();
    }
  }, [email, onRestart]);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setErrors({ email: '', forename: '', surname: '', telephone: '' });
    setSubmissionError('');

    // Validate
    let isValid = true;

    if (!email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Emailová adresa je povinná' }));
      isValid = false;
    } else if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Prosím vložte validnú emailovú adresu' }));
      isValid = false;
    }

    if (!forename.trim()) {
      setErrors(prev => ({ ...prev, forename: 'Meno je povinné' }));
      isValid = false;
    }

    if (isValid) {
      setIsSubmitting(true);

      try {
        const utmParameters = getUTMParameters();
        const userData: UserData = {
          email,
          forename,
          surname,
          telephone: telephone.trim() || undefined,
          utm: utmParameters || undefined
        };

        // Submit quiz results first
        await submitQuizResults(userData, categoryCounts, answers, questions);

        // If successful, proceed with the original onSubmit
        onSubmit(userData);
      } catch (error) {
        // Handle submission error
        const errorMessage = 'Spracovanie výsledkov sa nepodarilo.<br><br><b>' + ( error instanceof Error ?  error.message : 'Neznáma chyba, skúste to znova prosím.' ) + '</b>'
        setSubmissionError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
      <FormContainer>
        <IntroText>
          <p>
            <b>Sme radi, že ste si našli čas pre seba ❤️ a objavili váš jedinečný štýl šperkov! Veríme, že výsledky kvízu vám priniesú nové inšpirácie.</b>
          </p>
          <p>
            Posielame vám vaše personalizované výsledky a návrhy šperkov, ktoré presne vystihujú váš vkus. Stačí, ak nám zanecháte vaše meno a e-mailovú adresu.
          </p>
          <p>
            Môžete sa tešiť aj na pozvánky na naše eventy a exkluzívne ponuky.
          </p>
        </IntroText>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="forename">Meno*</Label>
            <Input
                type="text"
                id="forename"
                value={forename}
                onChange={(e) => setForename(e.target.value)}
                placeholder="Jana"
                className={errors.forename ? 'error' : ''}
                disabled={isSubmitting}
            />
            {errors.forename && <ErrorMessage>{errors.forename}</ErrorMessage>}
          </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Vaša emailová adresa*</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@gmail.com"
            className={errors.email ? 'error' : ''}
            disabled={isSubmitting}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>

          <CheckboxContainer>
            <CheckboxLabel>
              <b>Sľubujeme, že vaše údaje sú u nás v bezpečí a budeme vám posielať len to, čo vás naozaj zaujíma.</b> Pokračovaním súhlasíte so spracovaním osobných údajov.
            </CheckboxLabel>
          </CheckboxContainer>

        <Button $primary type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Spracovávam...' : 'Zobraziť výsledky'}
        </Button>

        {submissionError && (
            <SubmissionError>
              <span dangerouslySetInnerHTML={{ __html: submissionError }}></span>
              <br></br>
              <a href="#" onClick={onRestart} style={{ fontSize: '1.4rem', color: 'var(--color-danger)', display: 'block', marginTop: '1.2rem' }}><small>Reštartovať celý kvíz</small></a>
            </SubmissionError>
        )}
      </form>
    </FormContainer>
  );
};

export default EmailForm;
